import { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewArticleNotification({ onArticleClick }) {
  const { user } = useAuth();
  const [newArticles, setNewArticles] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const intervalRef = useRef(null);

  // Fonction pour vérifier les nouveaux articles depuis l'API (source de vérité)
  const checkForNewArticles = async () => {
    if (!user) return;

    try {
      const response = await axios.get(`${API_URL}/articles/notifications`);
      
      const notifications = response.data.notifications || [];
      
      // Filtrer uniquement ceux qui sont "unread" pour le popup automatique
      // Les "remind_later" apparaissent dans la cloche mais pas en popup automatique (sauf si on veut)
      const unreadNotifications = notifications.filter(n => n.notification_status === 'unread');

      if (unreadNotifications.length > 0) {
        setNewArticles(unreadNotifications);
        setShowNotification(true);
      } else {
        setShowNotification(false);
      }
    } catch (error) {
      console.error('Error checking for new articles:', error);
    }
  };

  // Polling toutes les 30 secondes
  useEffect(() => {
    if (!user) return;

    // Vérifier immédiatement au montage
    checkForNewArticles();

    // Puis vérifier toutes les 60 secondes pour ne pas spammer
    intervalRef.current = setInterval(checkForNewArticles, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user]);

  // Fonction pour marquer les articles comme lus (localement pour le popup)
  const markAsRead = () => {
    setShowNotification(false);
    // On ne vide pas newArticles ici pour permettre à "Voir maintenant" de l'utiliser
  };

  // Fonction pour gérer le clic sur "Voir maintenant"
  const handleViewNow = () => {
    markAsRead();
    if (onArticleClick && newArticles.length > 0) {
      onArticleClick(newArticles[0]);
    } else {
      window.location.hash = '#/infos';
    }
  };

  if (!showNotification || newArticles.length === 0) {
    return null;
  }

  return (
    <div className='fixed top-4 right-4 z-50 animate-in slide-in-from-top duration-300 w-[calc(100vw-2rem)] md:w-auto md:max-w-md'>
      <div className='bg-primary text-primary-foreground rounded-lg shadow-2xl p-4 border-2 border-primary-foreground/20'>
        <div className='flex items-start gap-3'>
          <div className='bg-primary-foreground/20 p-2 rounded-full'>
            <Bell className='size-5 animate-bounce' />
          </div>
          <div className='flex-1'>
            <h3 className='font-semibold text-base mb-1'>
              {newArticles.length === 1 
                ? 'Nouvel article disponible !' 
                : `${newArticles.length} nouveaux articles disponibles !`}
            </h3>
            <div className='space-y-2'>
              {newArticles.slice(0, 3).map((article, index) => (
                <div key={index} className='text-sm opacity-90'>
                  📝 {article.title}
                </div>
              ))}
              {newArticles.length > 3 && (
                <div className='text-sm opacity-75'>
                  ... et {newArticles.length - 3} autre{newArticles.length - 3 > 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div className='flex gap-2 mt-3'>
              <Button 
                size='sm' 
                variant='secondary'
                onClick={handleViewNow}
                className='text-xs'
              >
                Voir maintenant
              </Button>
              <Button 
                size='sm' 
                variant='ghost'
                onClick={markAsRead}
                className='text-xs hover:bg-primary-foreground/20'
              >
                Plus tard
              </Button>
            </div>
          </div>
          <Button
            variant='ghost'
            size='icon'
            className='size-6 hover:bg-primary-foreground/20'
            onClick={() => setShowNotification(false)}
          >
            <X className='size-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
